import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { EditorContent, useEditor } from '@tiptap/react';
import {
	DATA_CONTENT_EDITABLE_IDX,
	DATA_CONTENT_EDITABLE_TYPE,
	EditableProps,
	MergeTagBadge,
	useEditorContext,
	useEditorProps,
} from '@bay-email/editor';
import { useActiveEditable } from './ActiveEditableProvider';
import { Slice } from '@tiptap/pm/model';
import {
	EDITOR_ROOT_STYLE,
	buildTextBlockExtensions,
	normalizeLegacyInput,
	plainTextToFragment,
	toLegacyHtml,
	unwrapBorderWrap,
} from './extensions';
import { Point, resolveInitialSelection } from './initialSelection';
import { useFlushOnOutsidePress } from './useFlushOnOutsidePress';

export { ActiveEditableProvider, useActiveEditable } from './ActiveEditableProvider';
export type { ActiveEditable } from './ActiveEditableProvider';

interface DragPoints {
	down: Point | null;
	up: Point | null;
}

// Static until it becomes the active editable, so a table never mounts one
// editor per cell. The static element is the legacy one and keeps its stable
// key (set by the caller). RichTextField's click detection is what activates it.
export function TiptapEditable(props: EditableProps) {
	const { idx, type, tag, attributes, style, html } = props;
	const { active, setActive } = useActiveEditable();
	const pointRef = useRef<DragPoints | null>(null);
	// The Text block's render wraps every block in its border div; the stored
	// content never holds it, so the editor works on the inside.
	const content = useMemo(() => unwrapBorderWrap(html), [html]);

	if (active?.idx === idx) {
		return (
			<ActiveEditor
				key='active'
				{...props}
				html={content}
				pointRef={pointRef}
			/>
		);
	}

	return React.createElement(tag, {
		key: 'static',
		...attributes,
		// Marks the static element too so the legacy blur/input listeners never
		// write its stale innerHTML back while the editor is taking over.
		'data-editor': 'tiptap',
		style,
		onMouseDown: (e: React.MouseEvent) => {
			pointRef.current = { down: { x: e.clientX, y: e.clientY }, up: null };
		},
		onMouseUp: (e: React.MouseEvent) => {
			if (pointRef.current) pointRef.current.up = { x: e.clientX, y: e.clientY };
		},
		// Keyboard focus has no mousedown ahead of it; a pointer press already
		// activates on click, and mounting mid-press would cancel the drag.
		onFocus: () => {
			if (pointRef.current) return;
			setActive({ idx, type });
		},
		dangerouslySetInnerHTML: { __html: html },
	});
}

function ActiveEditor(props: EditableProps & { pointRef: React.MutableRefObject<DragPoints | null> }) {
	const { idx, type, tag, attributes, style, html, pointRef } = props;
	const { registerEditor } = useActiveEditable();
	const { formHelpers } = useEditorContext();
	const { mergeTagGenerate, enabledMergeTagsBadge } = useEditorProps();

	// The canvas keeps re-rendering `html` (resolved merge tags, brand colours)
	// while this is active; the editor owns the content from mount on.
	const mountHtml = useMemo(() => html, []); // eslint-disable-line react-hooks/exhaustive-deps
	const extensions = useMemo(() => buildTextBlockExtensions(), []);

	const latestRef = useRef<string | null>(null);
	const dirtyRef = useRef(false);
	const writeRef = useRef<(content: string) => void>(() => {});
	writeRef.current = (content: string) => {
		const next = enabledMergeTagsBadge
			? MergeTagBadge.revert(content, mergeTagGenerate)
			: content;
		formHelpers.change(idx, next);
		formHelpers.blur(idx);
	};

	const commit = useCallback(() => {
		if (!dirtyRef.current || latestRef.current === null) return;
		dirtyRef.current = false;
		writeRef.current(latestRef.current);
	}, []);
	const debouncedCommit = useMemo(() => debounce(commit, 200), [commit]);

	const editor = useEditor({
		extensions,
		content: normalizeLegacyInput(mountHtml),
		parseOptions: { preserveWhitespace: 'full' },
		editorProps: {
			attributes: {
				[DATA_CONTENT_EDITABLE_IDX]: idx,
				[DATA_CONTENT_EDITABLE_TYPE]: type,
				'data-editor': 'tiptap',
				style: EDITOR_ROOT_STYLE,
			},
			handlePaste: (view, event) => {
				const text = event.clipboardData?.getData('text/plain') || '';
				event.preventDefault();
				if (text) {
					const slice = new Slice(plainTextToFragment(view.state.schema, text), 0, 0);
					view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
				}
				return true;
			},
		},
		onUpdate: ({ editor: current }) => {
			latestRef.current = toLegacyHtml(current, mountHtml);
			dirtyRef.current = true;
			debouncedCommit();
		},
		onBlur: () => {
			// Only after an edit: the canvas shows transformed HTML, and persisting
			// it on a bare click-in/click-out would overwrite the stored template.
			if (!dirtyRef.current) return;
			debouncedCommit.cancel();
			commit();
		},
	});

	useEffect(() => {
		if (!editor) return;
		registerEditor(idx, editor);

		const points = pointRef.current;
		pointRef.current = null;
		const selection = resolveInitialSelection(
			({ x, y }) => editor.view.posAtCoords({ left: x, top: y })?.pos ?? null,
			points?.down ?? null,
			points?.up ?? null,
		);
		if (selection) {
			editor
				.chain()
				.focus()
				.setTextSelection({ from: selection.anchor, to: selection.head })
				.run();
		} else {
			editor.commands.focus('end');
		}

		return () => registerEditor(idx, null);
	}, [editor, idx, pointRef, registerEditor]);

	const flushPending = useCallback(() => {
		debouncedCommit.cancel();
		commit();
	}, [commit, debouncedCommit]);
	const getEditorDom = useMemo(() => (editor ? () => editor.view.dom : null), [editor]);
	useFlushOnOutsidePress(getEditorDom, flushPending);

	// Deactivating by clicking another block unmounts before blur can commit.
	useEffect(
		() => () => {
			debouncedCommit.cancel();
			commit();
		},
		[commit, debouncedCommit],
	);

	const { contenteditable: _contenteditable, ...wrapperAttributes } = attributes;

	return React.createElement(
		tag,
		{ ...wrapperAttributes, style },
		<EditorContent editor={editor} />,
	);
}
